var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { isEnterKey } from '../../utils/keys';
import EyeOn from '../../../assets/svg/redesign/eye-empty.svg';
import EyeOff from '../../../assets/svg/redesign/eye-off.svg';
var pinInputVariants = tv({
    slots: {
        root: 'flex justify-center w-full',
        // Center a fixed-width input container; eye sits in a reserved slot and won't shift centering.
        wrapper: 'relative inline-flex justify-center',
        inputContainer: 'relative inline-block',
        input: [
            // mimic old pin blocks (58px square, large centered glyphs)
            'h-[32"px] md:h-[58px]',
            'rounded-lg border border-gray-300 bg-white',
            // more vertical padding so content doesn't fill full height
            'px-4 py-2',
            // Use monospace to make tracking feel like equal sized blocks
            'font-mono',
            // slightly smaller font and line-height for better vertical balance
            'text-[18px] leading-[20px] md:text-[38px] md:leading-[40px] text-center',
            'uppercase outline-none',
            'focus:border-high-emphasis',
            // increase spacing a bit more
            'tracking-[0.9rem]',
            // clip any over-tall caret
            'overflow-hidden',
            // slightly softer caret
            'caret-high-emphasis/70',
        ].join(' '),
        // Reserve a fixed 32px slot for the eye so input centering never changes.
        eyeSlot: 'absolute left-full top-1/2 -translate-y-1/2 w-12 flex justify-center',
        // keep only sizing; button handles cursor/focus styles
        eye: 'size-6',
    },
});
var sanitizePin = function (raw, length) {
    return raw
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, length);
};
export var PinInput = function (_a) {
    var length = _a.length, pin = _a.pin, onChange = _a.onChange, onEnter = _a.onEnter, className = _a.className, wrongPin = _a.wrongPin;
    var classes = pinInputVariants();
    var ref = useRef(null);
    var _b = useState(false), showPin = _b[0], setShowPin = _b[1];
    var EyeIcon = showPin ? EyeOff : EyeOn;
    useEffect(function () {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var handleChange = function (e) {
        onChange(sanitizePin(e.currentTarget.value || '', length));
    };
    var handleKeyDown = function (e) {
        if (isEnterKey(e))
            onEnter === null || onEnter === void 0 ? void 0 : onEnter();
    };
    return (React.createElement("div", { className: "pin-input ".concat(classes.root(), " ").concat(className || '').trim() },
        React.createElement("div", { className: classes.wrapper() },
            React.createElement("div", { className: classes.inputContainer() },
                React.createElement("input", { ref: ref, type: showPin ? 'text' : 'password', value: pin, onChange: handleChange, onKeyDown: handleKeyDown, className: "pin-field dynamic-width ".concat(wrongPin ? 'wrong-pin' : ''), style: __assign({ '--len': String(length) }, (showPin ? {} : { WebkitTextSecurity: 'disc' })), maxLength: length, autoComplete: "one-time-code", inputMode: "text", pattern: "[A-Za-z0-9]*", autoCapitalize: "characters", autoCorrect: "off", spellCheck: false, "aria-label": "Sicherheits-PIN" }),
                React.createElement("div", { className: classes.eyeSlot() }, (pin.length > 0 || showPin) && (React.createElement("button", { type: "button", className: "toggle-pin", onMouseDown: function (e) {
                        // Prevent focus change but allow the event to continue
                        e.preventDefault();
                    }, onClick: function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPin(function (current) { return !current; });
                        // Keep input focused after toggle
                        setTimeout(function () { var _a; return (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
                    }, onTouchStart: function (e) {
                        // Prevent focus change on touch
                        e.preventDefault();
                    }, "aria-label": showPin ? 'PIN verbergen' : 'PIN anzeigen' },
                    React.createElement(EyeIcon, { className: classes.eye() }))))))));
};
